// The tournament winner's moment: shown once per device to everyone who opens the tournament
// after it ended, with confetti and a fanfare (sound can be turned off in the profile).
import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { motion } from 'motion/react';
import confetti from 'canvas-confetti';
import { t, money } from '../i18n/index.ts';
import { Avatar, Icon } from './ui.tsx';
import { playFanfare } from '../lib/sound.ts';

export interface Podium {
  place: number;
  /** "2." or, for a shared place, "2.–4.". */
  label: string;
  name: string;
  avatarUrl: string | null;
  prizeCents: number;
}

export function WinnerCelebration({
  tournamentId,
  tournamentName,
  podium,
  sound,
  onClose,
}: {
  tournamentId: string;
  tournamentName: string;
  podium: Podium[];
  sound: boolean;
  onClose: () => void;
}) {
  const closeBtn = useRef<HTMLButtonElement>(null);
  const winners = podium.filter((p) => p.place === 1);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    if (sound) playFanfare();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timers: number[] = [];
    if (!reduced) {
      const colors = ['#c9a227', '#f3e7c7', '#1f7479', '#e8e2d4', '#b8862b'];
      const burst = (x: number) => void confetti({ particleCount: 90, spread: 70, startVelocity: 45, origin: { x, y: 0.7 }, colors, zIndex: 300, disableForReducedMotion: true });
      burst(0.5);
      timers.push(window.setTimeout(() => burst(0.2), 350), window.setTimeout(() => burst(0.8), 650));
    }
    return () => {
      window.removeEventListener('keydown', onKey);
      for (const id of timers) window.clearTimeout(id);
      confetti.reset();
      previous?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const title = winners.length > 1 ? t('winner.titleTie') : t('winner.title');
  return (
    <motion.div className="winner-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        className="winner-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="winner-title"
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.8, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        <button ref={closeBtn} type="button" className="install-close" aria-label={t('app.close')} onClick={onClose}>
          <Icon name="close" />
        </button>
        <motion.div className="winner-trophy" aria-hidden="true" initial={{ rotate: -12, scale: 0.5 }} animate={{ rotate: 0, scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 10, delay: 0.15 }}>
          🏆
        </motion.div>
        <p className="winner-kicker">{tournamentName}</p>
        <h2 id="winner-title">{title}</h2>
        <div className="winner-names">
          {winners.map((w) => (
            <div key={w.name} className="winner-person">
              <Avatar name={w.name} url={w.avatarUrl} size="l" />
              <strong>{w.name}</strong>
              {w.prizeCents > 0 && <span className="winner-prize num">+{money(w.prizeCents)}</span>}
            </div>
          ))}
        </div>
        {podium.filter((p) => p.place > 1).length > 0 && (
          <ol className="winner-podium">
            {podium
              .filter((p) => p.place > 1)
              .map((p) => (
                <li key={`${p.place}-${p.name}`}>
                  <span className="podium-place">{p.label}</span>
                  <span className="podium-name">{p.name}</span>
                  {p.prizeCents > 0 && <span className="num">+{money(p.prizeCents)}</span>}
                </li>
              ))}
          </ol>
        )}
        <div className="btn-row winner-actions">
          <Link className="btn btn-primary" to={`/turnaukset/${tournamentId}/tulokset`} onClick={onClose}>
            {t('winner.showResults')}
          </Link>
          <button type="button" className="btn" onClick={onClose}>
            {t('app.close')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
